import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setDeleteIndividualPanelsMode, setTempPanelsGroup, togglePanelModal } from 'store/slices/PanelSlice';
import CancelDeletePanelModal from './CancelDeletePanelModal';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import useScreenDims from 'hooks/useScreenDims';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { useEffect } from 'react';
import useRemovePanelsQuery from 'hooks/api/panel/removePanel'; 
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';

export default function DeletePanelBtn() {
	const dispatch = useDispatch<AppDispatch>();
	const { deleteIndividualPanelMode, deletedPanels } = useSelector((state: RootState) => state.panelSlice.data);
	const { hadTransition } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { uuid, panel, designType } = useSelector((state: RootState) => state.roofData.data);
	const removePanelsMutation = useRemovePanelsQuery();
	const startShadingRecalc = useStartShadingRecalc();

	const screenDims = useScreenDims();
	const { width: screenWidth } = screenDims;
	const isMobileView = screenWidth < 860;

	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const isAnyOtherAction = useSelector(selectIsAnyToolActionActive) && !deleteIndividualPanelMode;
	const isRecomputing = useSelector(selectIsRecomputing);
	const shouldDisable = queriesRunningInBackground || isAnyOtherAction || hadTransition || isRecomputing;

	useEffect(() => {
		const cancelHandler = () => {
			dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
		};

		const doneHandler = async () => {
			if (!deletedPanels || deletedPanels.length === 0) {
				dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
				return;
			}

			const shadingStatus = await removePanelsMutation.mutateAsync();
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();

			dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
			await startShadingRecalc(shadingStatus);
		};

		registerToolModeHandlers(TOOL_ACTION.DELETE_PANEL, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard the deleted panels?',
		});

		return () => clearToolModeHandlers(TOOL_ACTION.DELETE_PANEL);
	}, [dispatch, removePanelsMutation, deletedPanels, uuid, panel?.key, designType, startShadingRecalc]);
	
	function handleClick() {
		if(deleteIndividualPanelMode && deletedPanels.length > 0){
			dispatch(togglePanelModal({targetModal: 'CANCEL_DELETE_PANEL', val:true}));
			return;
		}
	
		dispatch(setDeleteIndividualPanelsMode({ enabled: !deleteIndividualPanelMode }));
	}

	// function handleCancelCLick(event: React.MouseEvent<HTMLButtonElement>){
	// 	event.stopPropagation();
	// 	if(deletedPanels.length > 0){
	// 		dispatch(togglePanelModal({targetModal: 'CANCEL_DELETE_PANEL', val:true}));
	// 		return;
	// 	}

	// 	dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
	// }

	useEffect(() => {
		registerToolModeHandlers(TOOL_ACTION.DELETE_PANEL, { 
			cancel: () => {
				dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
				dispatch(setTempPanelsGroup({addedPanels: []}));
			},
			cancelConfirmMessage: 'Are you sure you want to discard deleted panels?',
		});
	
		return () => {
			clearToolModeHandlers(TOOL_ACTION.DELETE_PANEL);
		};
	}, [dispatch]);

	return (
		<>
			<Button style={{
				background: 'var(--defaultBgColor)',	
			}}
			id="delete-panel"
			className="btn-tools"
			disabled={shouldDisable}
			onClick={handleClick}
			tooltipContent={deleteIndividualPanelMode ? '' : 'Delete Panels'}
			>
				<svg 
					width={isMobileView ? '18.40' : '19.5'}
					height={isMobileView ? '20' : '21.5'} 
					viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M7.03074 22.6371H0.945312V1H17.1731V7.76159" stroke={`${deleteIndividualPanelMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M10.5 12.8V23H19.5V12.8H10.5Z" stroke={`${deleteIndividualPanelMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69" strokeLinejoin="round"/>
					<path d="M13.8 15.8V19.7M16.2 15.8V19.7M9 12.8H21" stroke={`${deleteIndividualPanelMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M11.6016 12.8L12.5883 10H17.4347L18.4016 12.8H11.6016Z" stroke={`${deleteIndividualPanelMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69" strokeLinejoin="round"/>
				</svg>

				{/* {deleteIndividualPanelMode && (
					<label className='buttonLabel'
						style={{
							color: `${deleteIndividualPanelMode ? '#003CFF' : '#64686A'}`,
						}}>
						Delete Panel
						<span className='buttonIcon' onClick={handleCancelCLick}>	
							<svg width="11" height="11" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M5.71132 4.50043L8.73856 1.47233C8.82035 1.39334 8.88559 1.29884 8.93048 1.19436C8.97536 
								1.08988 8.99898 0.97751 8.99997 0.863803C9.00096 0.750096 8.97929 0.637331 8.93623 0.532087C8.89317 
								0.426843 8.82958 0.331229 8.74918 0.250823C8.66877 0.170416 8.57316 0.106829 8.46791 0.0637703C8.36267 
								0.0207117 8.2499 -0.000955751 8.1362 3.23334e-05C8.02249 0.00102042 7.91012 0.0246443 7.80564 0.0695253C7.70116 
								0.114406 7.60667 0.179646 7.52767 0.261437L4.49957 3.28868L1.47233 0.261437C1.39334 0.179646 1.29884 0.114406 
								1.19436 0.0695253C1.08988 0.0246443 0.97751 0.00102042 0.863803 3.23334e-05C0.750096 -0.000955751 0.637331 
								0.0207117 0.532087 0.0637703C0.426843 0.106829 0.331229 0.170416 0.250823 0.250823C0.170416 0.331229 0.106829 
								0.426843 0.0637703 0.532087C0.0207117 0.637331 -0.000955751 0.750096 3.23334e-05 0.863803C0.00102042 0.97751 
								0.0246443 1.08988 0.0695253 1.19436C0.114406 1.29884 0.179646 1.39334 0.261437 1.47233L3.28868 4.49957L0.261437 
								7.52767C0.179646 7.60667 0.114406 7.70116 0.0695253 7.80564C0.0246443 7.91012 0.00102042 8.02249 3.23334e-05 
								8.1362C-0.000955751 8.2499 0.0207117 8.36267 0.0637703 8.46791C0.106829 8.57316 0.170416 8.66877 0.250823 
								8.74918C0.331229 8.82958 0.426843 8.89317 0.532087 8.93623C0.637331 8.97929 0.750096 9.00096 0.863803 8.99997C0.97751 
								8.99898 1.08988 8.97536 1.19436 8.93048C1.29884 8.88559 1.39334 8.82035 1.47233 8.73856L4.49957 5.71132L7.52767 
								8.73856C7.60667 8.82035 7.70116 8.88559 7.80564 8.93048C7.91012 8.97536 8.02249 8.99898 8.1362 8.99997C8.2499 
								9.00096 8.36267 8.97929 8.46791 8.93623C8.57316 8.89317 8.66877 8.82958 8.74918 8.74918C8.82958 8.66877 8.89317 
								8.57316 8.93623 8.46791C8.97929 8.36267 9.00096 8.2499 8.99997 8.1362C8.99898 8.02249 8.97536 7.91012 8.93048 
								7.80564C8.88559 7.70116 8.82035 7.60667 8.73856 7.52767L5.71132 4.50043Z" fill="#64686A" />
							</svg>
						</span>
					</label>
				)} */}
			</Button>
			<CancelDeletePanelModal />
		</>
	);
}